<template>
  <div
    v-for="item in combinedItems"
    :key="item.id"
    class="flex w-full flex-col items-center"
  >
    <div
      v-if="isClientPortalQuestionnaireSubmissionSchema(item)"
      class="w-full"
    >
      <div class="mt-5 flex flex-col items-center">
        <div
          class="mt-0 flex h-fit w-full flex-row flex-wrap items-center justify-between gap-3 md:flex-nowrap"
        >
          <div class="flex grow-0 items-center overflow-hidden">
            <ClientPortalQuestionaireIcon
              :class="{ 'mt-[21px]': item.state == 'CLOSED' }"
              :current="(item as Submission).answeredRequiredQuestionCount"
              :complete="(item as Submission).requiredQuestionCount"
            />
            <div class="inline-flex flex-col items-start justify-start gap-2 overflow-scroll">
              <div class="inline-flex w-full items-center justify-start gap-2">
                <div
                  class="white-space-nowrap min-w-0 max-w-[45vw] flex-shrink overflow-hidden truncate hyphens-auto text-wrap
                    break-words font-['Inter'] text-xl font-semibold text-black md:max-w-[27vw]"
                  lang="de"
                >
                  {{ (item as Submission).title }}
                </div>

                <div class="flex flex-col">
                  <Badge
                    v-if="getBadgeDetailsForSubmission(item).text"
                    :class="[
                      getBadgeDetailsForSubmission(item).bgColor,
                      getBadgeDetailsForSubmission(item).textColor,
                      getBadgeDetailsForSubmission(item).hoverBgColor
                    ]"
                    variant="default"
                  >
                    {{ getBadgeDetailsForSubmission(item).text }}
                  </Badge>
                </div>
              </div>
              <div class="w-full">
                <div
                  v-if="item.state !== 'CLOSED'"
                  class="flex flex-row"
                >
                  <PencilIcon class="ml-0 mr-1 mt-1 h-4 w-4" />
                  <div
                    class="whitespace-pre-wrap text-wrap pl-1 text-start leading-normal text-gray-700"
                  >
                    {{ (item as Submission).answeredQuestionCount }}/{{
                      (item as Submission).questionCount
                    }}
                    Fragen beantwortet
                  </div>
                </div>
                <div
                  v-if="item.state !== 'CLOSED'"
                  class="flex flex-row"
                >
                  <CalendarIcon class="ml-0 mr-1 mt-1 h-4 w-4" />
                  <div class="pl-1">
                    <span class="whitespace-nowrap text-gray-500">Fällig bis &nbsp;</span>
                    <span class="text-gray-500"></span>
                    <span class="whitespace-nowrap text-gray-500">
                      {{ formatDate((item as Submission).dueAt) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex grow flex-col justify-center">
            <div class="flex justify-end">
              <component
                :is="buttonType(item)?.component"
                @click="buttonType(item)?.onClick"
              />
            </div>
          </div>
        </div>
        <div class="mt-[29.67px] h-px w-[105%] bg-gray-200"></div>
      </div>
    </div>
    <div
      v-else-if="isRequest(item)"
      class="w-full"
    >
      <div
        class="mt-5 flex flex-col items-center"
        data-cy="clientPortalItemsPDF"
      >
        <div
          class="mt-0 flex h-fit w-full flex-row flex-wrap items-center justify-between gap-3 md:flex-nowrap"
        >
          <div class="flex grow-0 items-center overflow-hidden">
            <ClientPortalPdfIcon />
            <div
              class="inline-flex flex-col items-start justify-start gap-2 overflow-scroll"
              :class="(item as Request).response ? 'pt-[24px]' : ''"
            >
              <div class="inline-flex w-full items-center justify-start gap-2">
                <div
                  class="white-space-nowrap min-w-0 max-w-[45vw] flex-shrink overflow-hidden truncate hyphens-auto text-wrap
                    break-words font-['Inter'] text-xl font-semibold text-black md:max-w-[27vw]"
                  lang="de"
                >
                  {{ (item as Request).form_title }}
                </div>
                <div class="flex flex-col">
                  <Badge
                    :class="{
                      'bg-teal-100 text-teal-700': (item as Request).response,
                      'bg-red-100 text-red-700': !(item as Request).response
                    }"
                    variant="default"
                  >
                    {{ (item as Request).response ? 'Abgeschlossen' : 'Ausstehend' }}
                  </Badge>
                </div>
              </div>
              <div>
                <div
                  v-if="!(item as Request).response"
                  class="flex flex-row"
                >
                  <Info class="ml-0 mr-1 mt-1 h-4 w-4" />
                  <div
                    class="whitespace-pre-wrap text-balance pl-1 text-start font-['Inter'] text-base leading-normal
                      text-gray-700"
                  >
                    Bitte füllen Sie das Dokument aus
                  </div>
                </div>
                <div
                  v-if="!(item as Request).response"
                  class="flex flex-row"
                >
                  <CalendarIcon class="ml-0 mr-1 mt-1 h-4 w-4" />
                  <div class="pl-1">
                    <span class="whitespace-nowrap text-gray-500">Fällig bis &nbsp;</span>
                    <span class="font-['Inter'] text-gray-500"></span>
                    <span class="whitespace-nowrap font-['Inter'] text-gray-500">
                      {{ formatDate((item as Request).due_at) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="flex grow flex-col justify-center">
            <div class="flex items-center justify-end">
              <component
                :is="buttonType(item)?.component"
                @click="buttonType(item)?.onClick"
              />
            </div>
          </div>
        </div>
        <div class="mt-[29.67px] h-px w-[105%] bg-gray-200"></div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import Badge from '../ui/badge/Badge.vue'
  import { useClientActionStore } from '@/store/clientActionStore'
  import { useRouter } from 'vue-router'
  import { computed, onUpdated } from 'vue'
  import type {
    ActionItem,
    ClientPortalQuestionnaireSubmissionSchema as Submission,
    ClientPortalQuestionnaireSubmissionSchema,
    Request
  } from '@/types'
  import ClientPortalContinueButton from '@/components/clientPortal/clientPortalContinueButton.vue'
  import ClientPortalPdfIcon from '@/components/clientPortal/clientPortalPdfIcon.vue'
  import { getBadgeDetailsForSubmission } from '@/lib/utils'
  import ClientPortalQuestionaireIcon from '@/components/clientPortal/clientPortalQuestionaireIcon.vue'
  import ClientPortalStartButton from '@/components/clientPortal/clientPortalStartButton.vue'
  import ClientPortalCompletedButton from '@/components/clientPortal/clientPortalCompletedButton.vue'
  import { CalendarIcon, Info, PencilIcon } from 'lucide-vue-next'

  const router = useRouter()

  const clientActionsStore = useClientActionStore()
  onUpdated(() => {})

  const formatDate = (dateString: string | number | Date) => {
    const date = new Date(dateString)
    return new Intl.DateTimeFormat('de-DE', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }).format(date)
  }

  // Type guard to determine if an item is a ClientPortalQuestionnaireSubmissionSchema
  function isClientPortalQuestionnaireSubmissionSchema(
    item: ActionItem
  ): item is ClientPortalQuestionnaireSubmissionSchema {
    return item !== undefined && 'schema_' in item
  }

  // Type guard to determine if an item is a Request
  function isRequest(item: ActionItem): item is Request {
    return item !== undefined && 'response' in item
  }

  function getStatusPriority(item: ActionItem) {
    if (isClientPortalQuestionnaireSubmissionSchema(item)) {
      // It's a Submission
      if (item.state === 'OPEN') {
        return 1
      } else if (item.state === 'RECEIVED') {
        return item.answeredQuestionCount > 0 ? 1 : 2
      } else {
        return 3
      }
    } else if (isRequest(item)) {
      // It's a Request
      return item.response ? 3 : 2
    } else {
      // Handle unexpected item type
      console.error('Unexpected item type', item)
      return -1 // or any other appropriate handling
    }
  }

  const combinedItems = computed(() => {
    const submissions: ClientPortalQuestionnaireSubmissionSchema[] =
      clientActionsStore.getClientSubmissions.map((submission) => ({ ...submission }))
    const requests: any = clientActionsStore.getClientRequests.map((request) => ({ ...request }))
    // Concatenate the arrays and sort
    return [...submissions, ...requests].sort((a, b) => getStatusPriority(a) - getStatusPriority(b))
  })

  function navigateToQuestionaire(submissionId: any) {
    router.push({
      path: `/client/portal/questionnaire/${submissionId}`,
      query: { access_token: clientActionsStore.clientAccessToken }
    })
  }

  function navigateToDocument(document_token: any) {
    router.push({
      path: `/client/portal/document/${document_token}`,
      query: { access_token: clientActionsStore.clientAccessToken }
    })
  }

  const buttonType = (item: ActionItem) => {
    if (isRequest(item)) {
      return {
        component: item.response ? ClientPortalCompletedButton : ClientPortalStartButton,
        onClick: () => navigateToDocument(item.form_token)
      }
    } else if (isClientPortalQuestionnaireSubmissionSchema(item)) {
      const onClick = () => navigateToQuestionaire(item.shortId)
      if (item.state === 'RECEIVED') {
        return { component: ClientPortalStartButton, onClick }
      } else if (item.state === 'CLOSED') {
        return { component: ClientPortalCompletedButton, onClick }
      } else if (item.state === 'OPEN') {
        return { component: ClientPortalContinueButton, onClick }
      }
    }
    return null
  }
</script>
